import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; faker: any }
    | { kind: 'error'; message: string };

const LOCALES = [
    { value: 'en_US', label: 'USA (+1)' },
    { value: 'en_GB', label: 'UK (+44)' },
    { value: 'en_CA', label: 'Canada (+1)' },
    { value: 'fr', label: 'France (+33)' },
    { value: 'de', label: 'Germany (+49)' },
    { value: 'it', label: 'Italy (+39)' },
    { value: 'es', label: 'Spain (+34)' },
    { value: 'ja', label: 'Japan (+81)' },
    { value: 'ko', label: 'Korea (+82)' },
    { value: 'zh_CN', label: 'China (+86)' },
    { value: 'pt_BR', label: 'Brazil (+55)' },
    { value: 'hi_IN', label: 'India (+91)' },
];

interface State {
    locale: string;
    format: 'e164' | 'national' | 'international';
    count: number;
    output: string;
    lib: LibState;
    copied: boolean;
}

export default class FakePhoneGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        locale: 'en_US',
        format: 'national',
        count: 10,
        output: '',
        lib: { kind: 'idle' },
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async buildFaker(): Promise<any | null> {
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('@faker-js/faker');
            const all = mod.allLocales as Record<string, any>;
            const localeData = all[this.state.locale] || all.en;
            const faker = new mod.Faker({ locale: [localeData, all.en, all.base] });
            this.setState({ lib: { kind: 'ready', faker } });
            return faker;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load faker' } });
            return null;
        }
    }

    generate = async () => {
        const faker = await this.buildFaker();
        if (!faker) return;
        const lines: string[] = [];
        for (let i = 0; i < this.state.count; i++) {
            const num = faker.phone.number();
            lines.push(num);
        }
        this.setState({ output: lines.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { locale, count, output, lib, copied } = this.state;
        const phoneCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="fp-locale">Country / locale</label>
                        <select id="fp-locale" value={locale}
                            onChange={e => this.setState({ locale: e.target.value, lib: { kind: 'idle' } }, this.generate)}>
                            {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="fp-count">Number of phones</label>
                        <input id="fp-count" type="number" inputMode="decimal" min={1} max={500} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(500, +e.target.value || 10)) }, this.generate)} />
                    </div>
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                    disabled={lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                    {lib.kind === 'loading' ? 'Loading faker…' : '↻ Regenerate'}
                </button>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    Faker emits whatever phone format the locale supplies — formats vary by region.
                </p>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated phones"
                previewMeta={`${phoneCount} number${phoneCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated phone numbers">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
                error={lib.kind === 'error' ? (lib as any).message : null}
            />
        );
    }
}
